/**
 * Bulk edit bar - changes apply immediately to selected items
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { useRef, useState } from 'react';
import { Trash2, X } from 'lucide-react';
import {
  CLICK_ID_STRUCTURE_OPTIONS,
  DOMAIN_SCHEME_OPTIONS,
  isShortlinkClickIdStructure,
  isShortlinkDomainScheme,
} from '@trafficpulse/shared';

import { Button } from '@/components/ui';
import { Checkbox } from '@/components/ui';
import { Input } from '@/components/ui';
import { SearchableSelect } from '@/components/ui';
import { useStaticTags } from '@/hooks';
import type { EditableItem } from './useEditPresetForm';
import type { PresetsModalCatalog } from '../shared';

export interface EditPresetBulkBarProps {
  count: number;
  catalog: PresetsModalCatalog;
  onApply: (patch: Partial<EditableItem>) => void;
  onClear: () => void;
  onRemove: () => void;
}

type SelectField = {
  key: string;
  label: string;
  opts: { value: number | string; label: string }[];
  value: number | string;
  setValue: (v: number | string) => void;
  getPatch: (v: number | string) => Partial<EditableItem> | null;
};

// Prefixed: a picked select shows only its label, and a bare "None" says nothing.
const CLICK_ID_OPTS = CLICK_ID_STRUCTURE_OPTIONS.map((o) => ({ value: o.value, label: `ID: ${o.label}` }));
const SCHEME_OPTS = DOMAIN_SCHEME_OPTIONS.map((o) => ({ value: o.value, label: `Scheme: ${o.label}` }));

export const EditPresetBulkBar: FC<EditPresetBulkBarProps> = ({
  count,
  catalog,
  onApply,
  onClear,
  onRemove,
}) => {
  const { data: staticTags } = useStaticTags();
  const [campaignId, setCampaignId] = useState<number>(0);
  const [routeId, setRouteId] = useState<number>(0);
  const [leadsId, setLeadsId] = useState<number>(0);
  const [tldValue, setTldValue] = useState<string>('');
  const [tagId, setTagId] = useState<string>('');
  const [amount, setAmount] = useState<string>('');
  const [forceSend, setForceSend] = useState<boolean | null>(null);
  const [useOnlyNewDomains, setUseOnlyNewDomains] = useState<boolean | null>(null);
  const [clickId, setClickId] = useState<string>('');
  const [scheme, setScheme] = useState<string>('');

  const amountRef = useRef(amount);
  amountRef.current = amount;

  const selectFields: SelectField[] = [
    { key: 'campaign', label: 'Campaign', opts: catalog.campaigns.map((c) => ({ value: c.id, label: c.name })),
      value: campaignId, setValue: (v) => setCampaignId(Number(v)),
      getPatch: (v) => (Number(v) > 0 ? { campaignWiserId: Number(v) } : null) },
    { key: 'gateway', label: 'Gateway', opts: catalog.gateways.map((g) => ({ value: g.id, label: g.name })),
      value: routeId, setValue: (v) => setRouteId(Number(v)),
      getPatch: (v) => (Number(v) > 0 ? { routeId: Number(v) } : null) },
    { key: 'leads', label: 'Leads', opts: catalog.leadsLists.map((l) => ({ value: l.id, label: l.name })),
      value: leadsId, setValue: (v) => setLeadsId(Number(v)),
      getPatch: (v) => (Number(v) > 0 ? { leadsListWiserId: Number(v) } : null) },
    { key: 'tld', label: 'TLD', opts: catalog.tlds.map((t) => ({ value: t.value, label: t.displayName })),
      value: tldValue, setValue: (v) => setTldValue(String(v)),
      getPatch: (v) => (String(v).trim() ? { shortLinkTldValue: String(v) } : null) },
    { key: 'tag', label: 'Tag', opts: (staticTags ?? []).map((t) => ({ value: t.id, label: t.name })),
      value: tagId, setValue: (v) => setTagId(String(v)),
      getPatch: (v) => (String(v).trim() ? { staticTagId: String(v) } : null) },
    { key: 'clickId', label: 'Click ID', opts: CLICK_ID_OPTS, value: clickId, setValue: (v) => setClickId(String(v)),
      getPatch: (v) => (isShortlinkClickIdStructure(v) ? { clickIdStructure: v } : null) },
    { key: 'scheme', label: 'Scheme', opts: SCHEME_OPTS, value: scheme, setValue: (v) => setScheme(String(v)),
      getPatch: (v) => (isShortlinkDomainScheme(v) ? { domainScheme: v } : null) },
  ];

  const handleSelectChange = (f: SelectField, v: number | string) => {
    f.setValue(v);
    const patch = f.getPatch(v);
    if (patch) onApply(patch);
  };

  const applyAmount = () => {
    const n = parseInt(amountRef.current, 10);
    if (n > 0) onApply({ amount: n });
  };

  const handleOptionChange = (key: 'forceSend' | 'useOnlyNewDomains', checked: boolean) => {
    if (key === 'forceSend') setForceSend(checked);
    else setUseOnlyNewDomains(checked);
    onApply({ [key]: checked });
  };

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground">Apply to {count} selected</span>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={onRemove} className="h-6 gap-1 text-destructive hover:text-destructive" aria-label="Remove selected">
            <Trash2 className="h-3.5 w-3.5" />
            Remove
          </Button>
          <Button size="sm" variant="ghost" onClick={onClear} className="h-6 px-1.5" aria-label="Clear selection">
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {selectFields.map((f) => (
          <div key={f.key} className="w-[100px] shrink-0">
            <SearchableSelect
              options={f.opts}
              value={f.value}
              onChange={(v) => handleSelectChange(f, v)}
              placeholder={f.label}
              className="h-7 text-xs"
            />
          </div>
        ))}
        <div className="min-w-[4.5rem] w-20 shrink-0">
          <Input
            type="number"
            min={1}
            placeholder="Amt"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            onBlur={applyAmount}
            onKeyDown={(e) => e.key === 'Enter' && applyAmount()}
            className="h-7 text-xs"
            aria-label="Amount"
          />
        </div>
        <label className="flex cursor-pointer items-center gap-1.5 text-xs">
          <Checkbox
            checked={forceSend ?? false}
            onCheckedChange={(c) => handleOptionChange('forceSend', Boolean(c))}
          />
          <span>Include duplicates</span>
        </label>
        <label className="flex cursor-pointer items-center gap-1.5 text-xs">
          <Checkbox
            checked={useOnlyNewDomains ?? false}
            onCheckedChange={(c) => handleOptionChange('useOnlyNewDomains', Boolean(c))}
          />
          <span>New domains only</span>
        </label>
      </div>
    </div>
  );
};
